/**
 * THE ONLY IndexedDB CODE IN THE APP.
 * ===================================
 *
 * Hand-rolled rather than `idb`, for two reasons that are about failure modes and not about
 * dependency counts:
 *
 *  1. **Every write in this app is a durability promise to a technician**, so the code that decides
 *     when a write is "done" has to be readable in one sitting. Here it is: a write resolves on the
 *     TRANSACTION's `complete` event, never on the request's `success` event. A request can succeed
 *     inside a transaction that later aborts (quota, a version change, the tab being killed); code
 *     that resolves on `success` reports a submission as saved that is not saved. That single line
 *     is the whole reason this file exists.
 *  2. `blob`/`outbox` are two stores with no relations. There is nothing here for a wrapper to
 *     simplify.
 *
 * SCHEMA (version 1)
 * ------------------
 * · `outbox`  — keyPath `id`. Index `byState` on `state`, and `byNextAttemptAt` on `nextAttemptAt`
 *               so the flusher can find due work without scanning. `nextAttemptAt` is optional on
 *               the item, and IndexedDB simply omits missing keys from an index, which is the
 *               behaviour we want: a `queued` item with no schedule is due immediately and is found
 *               through `byState` instead.
 * · `blobs`   — keyPath `blobId`. Photo bytes live here, NOT on the outbox row: a 4 MB photo inlined
 *               into the queue row would be read, structured-cloned and rewritten on every single
 *               state transition of that row.
 *
 * ⚠️ **A `versionchange` HANDLER IS MANDATORY, NOT HOUSEKEEPING.** If a second tab opens a newer
 * schema and this tab holds the connection open, the upgrade BLOCKS and both tabs hang — the
 * technician sees a frozen app with their work inside it. So we close on `versionchange` and mark
 * the handle stale; the next operation reopens.
 */

export const DB_NAME = 'lockercustody';
export const DB_VERSION = 1;
export const OUTBOX_STORE = 'outbox';
export const BLOB_STORE = 'blobs';

let handle: Promise<IDBDatabase> | null = null;

function indexedDbOrThrow(): IDBFactory {
  const factory: IDBFactory | undefined = globalThis.indexedDB;
  if (factory === undefined) {
    /**
     * No fallback to `localStorage`, deliberately. `localStorage` is synchronous, ~5 MB, cannot
     * hold a Blob, and is evicted at least as eagerly as IndexedDB — a fallback would look like a
     * durable queue and behave like a scratchpad. Refusing loudly means the UI can tell the
     * technician not to trust the device, which is the honest answer.
     */
    throw new Error('IndexedDB unavailable: this device cannot hold work offline');
  }
  return factory;
}

export function openDb(): Promise<IDBDatabase> {
  if (handle !== null) {
    return handle;
  }
  handle = new Promise<IDBDatabase>((resolvePromise, rejectPromise) => {
    const request = indexedDbOrThrow().open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        const outbox = db.createObjectStore(OUTBOX_STORE, { keyPath: 'id' });
        outbox.createIndex('byState', 'state', { unique: false });
        outbox.createIndex('byNextAttemptAt', 'nextAttemptAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        db.createObjectStore(BLOB_STORE, { keyPath: 'blobId' });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        // See the header: holding this open would block another tab's upgrade and hang both.
        db.close();
        handle = null;
      };
      db.onclose = () => {
        handle = null;
      };
      resolvePromise(db);
    };

    request.onerror = () => {
      handle = null;
      rejectPromise(request.error ?? new Error('IndexedDB open failed'));
    };

    request.onblocked = () => {
      handle = null;
      rejectPromise(new Error('IndexedDB open blocked by another tab holding an old version'));
    };
  });
  return handle;
}

/**
 * Run work in a transaction and resolve **when the transaction completes**.
 *
 * The `readwrite` case is the important one: `resolve` is wired to `transaction.oncomplete`, so a
 * caller that awaits this has a durability guarantee rather than an acknowledgement. Contrast
 * resolving on the request's `success`, which is true *"the browser accepted this"* and not
 * *"this survived"*.
 */
export async function withTransaction<T>(
  storeNames: string | string[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolvePromise, rejectPromise) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(storeNames, mode);
    } catch (error) {
      rejectPromise(error);
      return;
    }

    let result: T;
    let settled = false;
    const fail = (error: unknown): void => {
      if (settled) {
        return;
      }
      settled = true;
      rejectPromise(error instanceof Error ? error : new Error(String(error)));
    };

    tx.oncomplete = () => {
      if (settled) {
        return;
      }
      settled = true;
      resolvePromise(result);
    };
    tx.onerror = () => fail(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => fail(tx.error ?? new Error('IndexedDB transaction aborted'));

    void (async () => {
      try {
        result = await work(tx);
      } catch (error) {
        // Abort explicitly so a thrown callback cannot leave a half-applied transaction to
        // commit on its own.
        try {
          tx.abort();
        } catch {
          // Already finished; the abort/error handler above has (or will) settle the promise.
        }
        fail(error);
      }
    })();
  });
}

/** Promisifies a single `IDBRequest`. Never used to signal durability — see the header. */
export function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolvePromise, rejectPromise) => {
    req.onsuccess = () => resolvePromise(req.result);
    req.onerror = () => rejectPromise(req.error ?? new Error('IndexedDB request failed'));
  });
}

/**
 * Drops the cached connection so the next call reopens.
 *
 * Exported for tests, where it is what makes "survives a reload" a real assertion rather than a
 * read of a module-level cache: the test closes the handle, forgets it, and reopens the SAME
 * database. It is also correct in production after a `versionchange`.
 */
export function closeDb(): void {
  const current = handle;
  handle = null;
  if (current !== null) {
    void current.then((db) => db.close()).catch(() => undefined);
  }
}
